<template>
  <Teleport to="body">
    <Transition name="toast">
      <div
        v-if="visible"
        :class="[
          'fixed top-4 right-4 z-50 max-w-md w-full bg-white rounded-xl shadow-2xl border-l-4 overflow-hidden',
          typeClasses
        ]"
      >
        <div class="p-4 flex items-start gap-3">
          <div class="flex-shrink-0">
            <CheckCircle v-if="type === 'success'" class="w-6 h-6 text-green-600" />
            <XCircle v-if="type === 'error'" class="w-6 h-6 text-red-600" />
            <AlertCircle v-if="type === 'warning'" class="w-6 h-6 text-orange-600" />
            <Info v-if="type === 'info'" class="w-6 h-6 text-blue-600" />
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-semibold text-gray-900 mb-1">{{ title }}</h4>
            <p class="text-sm text-gray-600">{{ message }}</p>
          </div>
          <button
            @click="close"
            class="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X class="w-5 h-5" />
          </button>
        </div>
        <div class="h-1 bg-gray-100">
          <div
            class="h-full transition-all duration-100 ease-linear"
            :class="progressClass"
            :style="{ width: progress + '%' }"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-vue-next'

interface Props {
  type?: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  duration: 5000
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const visible = ref(false)
const progress = ref(100)
let timer: number | null = null
let progressTimer: number | null = null

const typeClasses = computed(() => {
  const classes = {
    success: 'border-green-500',
    error: 'border-red-500',
    warning: 'border-orange-500',
    info: 'border-blue-500'
  }
  return classes[props.type]
})

const progressClass = computed(() => {
  const classes = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-orange-500',
    info: 'bg-blue-500'
  }
  return classes[props.type]
})

const close = () => {
  visible.value = false
  if (timer) clearTimeout(timer)
  if (progressTimer) clearInterval(progressTimer)
  setTimeout(() => emit('close'), 300)
}

onMounted(() => {
  visible.value = true
  
  // Progress bar
  const interval = 50
  const step = (interval / props.duration) * 100
  progressTimer = window.setInterval(() => {
    progress.value -= step
    if (progress.value <= 0) {
      progress.value = 0
      if (progressTimer) clearInterval(progressTimer)
    }
  }, interval)
  
  // Auto close
  timer = window.setTimeout(close, props.duration)
})

onUnmounted(() => {
  if (timer) clearTimeout(timer)
  if (progressTimer) clearInterval(progressTimer)
})
</script>

<style scoped>
.toast-enter-active {
  animation: slideIn 0.3s ease-out;
}

.toast-leave-active {
  animation: slideOut 0.3s ease-in;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}
</style>
